import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { InfoCard } from '@backstage/core-components';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import BuildIcon from '@material-ui/icons/Build';
import PaletteIcon from '@material-ui/icons/Palette';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import { useEnv } from './EnvContext';
import { EntitySparrowDeskSelectorBar } from './EntitySparrowDeskSelectorBar';
import { findApp, urlsForApp } from '../coalitions';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  path: {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
    wordBreak: 'break-all',
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

function pipelineIcon(pipeline: string): React.ReactNode {
  if (pipeline === 'FrontendDeployments') return <PaletteIcon color="primary" />;
  return <BuildIcon color="primary" />;
}

function pipelineTitle(pipeline: string): string {
  if (pipeline === 'BackendDeployments') return 'Backend deployment';
  if (pipeline === 'FrontendDeployments') return 'Frontend deployment';
  return pipeline;
}

// Env-aware Jenkins tab. Renders one card per pipeline the coalition
// declares that this app opts into (respecting hasBE / hasFE).
export const EntitySparrowDeskJenkinsContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const { coalition, env, tier } = useEnv(entity, config);

  const app = coalition ? findApp(coalition, entity) : undefined;

  if (!coalition || !app || !env) {
    return (
      <Box className={classes.emptyState}>
        <Typography>Jenkins tab isn't configured for this entity.</Typography>
      </Box>
    );
  }

  const urls = urlsForApp(app, env, coalition);
  if (!urls) return null;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <EntitySparrowDeskSelectorBar />
      </Grid>

      {urls.jenkins.length === 0 && (
        <Grid item xs={12}>
          <InfoCard title="Jenkins pipelines">
            <Typography color="textSecondary">
              <strong>{app.name}</strong> has no deploy pipeline in this
              coalition ({coalition.pipelines.join(', ') || 'none configured'}).
            </Typography>
          </InfoCard>
        </Grid>
      )}

      {urls.jenkins.map(j => (
        <Grid item md={6} xs={12} key={j.pipeline}>
          <InfoCard title={`${pipelineTitle(j.pipeline)} — ${env}`}>
            <Box className={classes.header}>
              {pipelineIcon(j.pipeline)}
              <Typography variant="h6" component="div">
                {app.name} · {j.pipeline}
              </Typography>
              <Chip
                size="small"
                label={env}
                style={{
                  background: tier === 'production' ? '#dbeafe' : '#fef3c7',
                  color: tier === 'production' ? '#1e40af' : '#92400e',
                  marginLeft: 'auto',
                }}
              />
            </Box>
            <Typography variant="body2" color="textSecondary">
              Pipeline URL:
            </Typography>
            <Typography className={classes.path}>{j.url.replace(/^https?:\/\//, '')}</Typography>
            <Button
              variant="contained"
              color="primary"
              endIcon={<OpenInNewIcon />}
              href={j.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open pipeline
            </Button>
          </InfoCard>
        </Grid>
      ))}
    </Grid>
  );
};

export default EntitySparrowDeskJenkinsContent;
